/**
 * Quantis Voice engine for a browser: wraps the calling SDK's Device and Call
 * in the shared VoiceEngine contract. Runs in the web app and, unchanged, in
 * the hidden web view the Mac app uses for audio (voice-engine.html).
 *
 * One call at a time. A second incoming call while one is live is declined so
 * the caller falls through to the office voicemail rather than ringing on top.
 */
import type { Call, Device } from '@twilio/voice-sdk';
import { createEmitter, type VoiceEngine, type VoiceEngineCommand } from '../../shared-ui/voice/voiceEngine';

export function createBrowserVoiceEngine(): VoiceEngine {
  const { emit, subscribe } = createEmitter();
  let device: Device | null = null;
  let current: Call | null = null;
  let pending = new Map<string, Call>();
  let player: HTMLAudioElement | null = null;
  let seq = 0;
  // Bumped by every start and stop, so a start still awaiting the SDK import
  // knows it was overtaken and does not leave a Device nobody can stop.
  let generation = 0;
  let dialling = false;
  let hangupRequested = false;

  const callId = (call: Call) => call.parameters?.CallSid || `local-${++seq}`;

  function track(call: Call, id: string, direction: 'inbound' | 'outbound') {
    call.on('ringing', () => emit({ type: 'state', callId: id, state: 'ringing', direction }));
    call.on('accept', () => {
      current = call;
      pending.delete(id);
      emit({ type: 'state', callId: id, state: 'open', direction });
    });
    call.on('mute', (muted: boolean) => emit({ type: 'muted', muted }));
    const closed = () => {
      pending.delete(id);
      if (current === call) current = null;
      emit({ type: 'state', callId: id, state: 'closed', direction });
    };
    call.on('disconnect', closed);
    call.on('cancel', closed);
    call.on('reject', closed);
    call.on('error', (error: { message?: string; code?: number }) =>
      emit({ type: 'error', message: error?.message || 'The call failed', code: error?.code }));
  }

  async function start(token: string) {
    const mine = ++generation;
    const { Device: DeviceClass, Call: CallClass } = await import('@twilio/voice-sdk');
    if (mine !== generation) return;
    device?.destroy();
    device = new DeviceClass(token, {
      // Dublin keeps media for a UK office on the nearest edge.
      edge: ['dublin', 'roaming'],
      codecPreferences: [CallClass.Codec.Opus, CallClass.Codec.PCMU],
      closeProtection: 'You are on a call. Leaving will hang up.',
      tokenRefreshMs: 60_000,
    });
    device.on('registered', () => emit({ type: 'registered' }));
    device.on('unregistered', () => emit({ type: 'unregistered' }));
    device.on('tokenWillExpire', () => emit({ type: 'tokenWillExpire' }));
    device.on('error', (error: { message?: string; code?: number }) =>
      emit({ type: 'error', message: error?.message || 'Quantis Voice lost its connection', code: error?.code }));
    device.on('incoming', (call: Call) => {
      // One call at a time: a second caller while one rings or talks goes to voicemail.
      if (current || dialling || pending.size > 0) { call.reject(); return; }
      const id = callId(call);
      pending.set(id, call);
      track(call, id, 'inbound');
      const name = call.customParameters?.get('callerName') || null;
      const lineName = call.customParameters?.get('lineName') || null;
      emit({ type: 'incoming', callId: id, from: call.parameters?.From ?? '', callerName: name, lineName });
    });
    await device.register();
  }

  async function dial(to: string) {
    if (!device) throw new Error('Quantis Voice is not connected yet');
    if (current || dialling) throw new Error('Hang up the current call first');
    const id = `out-${++seq}`;
    dialling = true;
    try {
      const call = await device.connect({ params: { To: to } });
      current = call;
      track(call, id, 'outbound');
      emit({ type: 'state', callId: id, state: 'connecting', direction: 'outbound' });
      // Hang up pressed while the call was still being set up.
      if (hangupRequested) { hangupRequested = false; call.disconnect(); }
    } catch (error) {
      emit({ type: 'state', callId: id, state: 'closed', direction: 'outbound' });
      throw error;
    } finally {
      dialling = false;
    }
  }

  /** This device's own British English voice: close to, not the same as, what callers hear. */
  function speak(text: string) {
    if (typeof speechSynthesis === 'undefined' || !text.trim()) return;
    speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-GB';
    const voice = speechSynthesis.getVoices().find((v) => v.lang === 'en-GB');
    if (voice) utterance.voice = voice;
    utterance.onend = () => emit({ type: 'playback', playing: false });
    emit({ type: 'playback', playing: true });
    speechSynthesis.speak(utterance);
  }

  function play(url: string) {
    player?.pause();
    player = new Audio(url);
    player.onended = () => emit({ type: 'playback', playing: false });
    player.onpause = () => emit({ type: 'playback', playing: false });
    void player.play().then(
      () => emit({ type: 'playback', playing: true }),
      () => emit({ type: 'error', message: 'The voicemail could not be played' }),
    );
  }

  function handle(command: VoiceEngineCommand) {
    switch (command.type) {
      case 'start':
        start(command.token).catch((error) => emit({ type: 'error', message: error?.message || 'Quantis Voice could not start' }));
        break;
      case 'updateToken': device?.updateToken(command.token); break;
      case 'call':
        dial(command.to).catch((error) => emit({ type: 'error', message: error?.message || 'The call could not be placed' }));
        break;
      case 'accept': pending.get(command.callId)?.accept(); break;
      case 'reject': pending.get(command.callId)?.reject(); break;
      case 'hangup':
        if (dialling) hangupRequested = true;
        current?.disconnect();
        pending.forEach((call) => call.reject());
        break;
      case 'mute': current?.mute(command.muted); break;
      case 'digits':
        if (/^[0-9*#w]+$/.test(command.digits)) current?.sendDigits(command.digits);
        break;
      case 'play': play(command.url); break;
      case 'stopPlayback': player?.pause(); globalThis.speechSynthesis?.cancel(); break;
      case 'speak': speak(command.text); break;
      case 'stop':
        generation++;
        device?.destroy();
        device = null;
        current = null;
        pending = new Map();
        player?.pause();
        break;
    }
  }

  return { send: handle, subscribe };
}
